import { useEffect, useState } from "react";
import {
  Messages,
  NotificationSeverity,
  TABLE_ACTIONS,
} from "helpers/constants";
import ConfirmActionModal from "modals/ConfirmActionModal/ConfirmActionModal";
import CustomTable from "components/Table/CustomTable";
import { Species } from "types/species";
import {
  useDeleteSpeciesMutation,
  useGetSpeciesQuery,
} from "graphql/__generated__/hooks";
import { MainLayoutContainer } from "components/Layout";
import { Add } from "@mui/icons-material";
import CreateOrEditSpeciesModal from "modals/CreateOrEditSpeciesModal/CreateOrEditSpeciesModal";
import { ResponsiveButton } from "components/Button";
import { useNotifications } from "hooks/useNotifications";

export function SpeciesList() {
  const [createSpeciesModal, setCreateSpeciesModal] = useState(false);
  const [deleteSpeciesModal, setDeleteSpeciesModal] = useState(false);
  const [speciesIdToDelete, setSpeciesIdToDelete] = useState("");
  const [species, setSpecies] = useState([] as Species[]);
  const [selectedSpecies, setSelectedSpecies] = useState<Species | null>(null);
  const [deleteSpecies] = useDeleteSpeciesMutation();
  const { loading, error, data, refetch } = useGetSpeciesQuery();
  const { notify } = useNotifications();

  useEffect(() => {
    setSpecies((data?.species as Species[]) || []);
  }, [data]);

  const handleActionClick = (action: TABLE_ACTIONS, id: string) => {
    switch (action) {
      case TABLE_ACTIONS.EDIT:
        const speciesToEdit = species?.find(
          (species) => species.species_id === id,
        );
        if (speciesToEdit) {
          setSelectedSpecies(speciesToEdit);
          setCreateSpeciesModal(true);
        }
        break;
      case TABLE_ACTIONS.DELETE:
        setSpeciesIdToDelete(id);
        setDeleteSpeciesModal(true);
        break;
      default:
        break;
    }
  };

  const handleCreateSpeciesModalOpen = () => {
    setCreateSpeciesModal(true);
  };

  const handleCreateOrEditSpeciesChange = () => {
    if (selectedSpecies !== null) {
      setSelectedSpecies(null);
    }
    setCreateSpeciesModal(false);
    refetch().then();
  };

  const handleDelete = async () => {
    if (!speciesIdToDelete || speciesIdToDelete === "") {
      return;
    }
    await deleteSpecies({ variables: { id: speciesIdToDelete } });
    await refetch();
    setSpeciesIdToDelete("");
    notify({
      message: Messages.successSpeciesDeleted,
      severity: NotificationSeverity.success,
    });
  };

  return (
    <MainLayoutContainer
      title="species"
      loading={loading}
      error={error}
      buttonActions={
        <ResponsiveButton
          onClick={() => handleCreateSpeciesModalOpen()}
          icon={<Add />}
        >
          New Species
        </ResponsiveButton>
      }
    >
      <CustomTable
        entities={species}
        handleActionClick={handleActionClick}
        fieldToFilter={"name"}
        columns={[
          {
            id: "name",
            label: "Name",
            value: "name",
          },
          { id: "actions", value: "" },
        ]}
        isSelectable={false}
        allowedMenuItems={[TABLE_ACTIONS.EDIT, TABLE_ACTIONS.DELETE]}
        extraActionItems={[]}
        idColumn={"species_id"}
      />

      {createSpeciesModal && (
        <CreateOrEditSpeciesModal
          handleCreateOrEditSpeciesChange={handleCreateOrEditSpeciesChange}
          createOrEditSpeciesModal={createSpeciesModal}
          handleCreateOrEditSpeciesModalDismiss={() => {
            setCreateSpeciesModal(false);
            if (selectedSpecies !== null) {
              setSelectedSpecies(null);
            }
          }}
          species={selectedSpecies}
        />
      )}

      {deleteSpeciesModal && (
        <ConfirmActionModal
          title={"Heads up!"}
          message={"Are you sure you want to delete this species?"}
          show={deleteSpeciesModal}
          onConfirm={handleDelete}
          onDismiss={() => {
            setDeleteSpeciesModal(false);
          }}
        />
      )}
    </MainLayoutContainer>
  );
}
